import { useEffect, useState } from 'react';
import { get } from './api.js';
import { Link, matchRoute, navigate, useLocation } from './router.jsx';
import { Icon, LangButton, Logo, ThemeButton } from './ui.jsx';
import { Guard, useAuth } from './session.jsx';
import { Home, Search, Property } from './browse.jsx';
import { Login, Register, Forgot } from './auth.jsx';
import { PostWizard } from './post.jsx';
import { Profile, MyListings, Leads, Chat, Saved, Notifications, Verify, Settings } from './account.jsx';
import { Plans, Credits, Ownership } from './billing.jsx';
import { Welcome } from './welcome.jsx';
import { Terms, Privacy } from './legal.jsx';
import { Agents, ListerProfile } from './reviews.jsx';
import { About, Footer, Help, Trust } from './company.jsx';
import { t } from './i18n.js';
import { Admin } from './admin.jsx';
import { BuyingGuide, OwnerHub, ViewingPass } from './premium.jsx';

const guarded = (Component, roles) => (props) => <Guard roles={roles}><Component {...props} /></Guard>;
const LISTERS = ['owner', 'agent'];

const ROUTES = [
  ['/', Home], ['/search', Search], ['/p/:id', Property], ['/u/:id', ListerProfile], ['/agents', Agents], ['/about', About], ['/trust', Trust], ['/help', Help], ['/guide', BuyingGuide],
  ['/terms', Terms], ['/privacy', Privacy], ['/login', Login], ['/register', Register], ['/forgot', Forgot],
  ['/post', guarded(PostWizard, LISTERS)], ['/post/:id', guarded(PostWizard, LISTERS)],
  ['/me', guarded(Profile)], ['/me/listings', guarded(MyListings, LISTERS)], ['/me/listings/:id/ownership', guarded(Ownership, LISTERS)], ['/me/credits', guarded(Credits, LISTERS)], ['/me/leads', guarded(Leads)], ['/me/leads/:id/pass', guarded(ViewingPass)], ['/me/hub', guarded(OwnerHub, LISTERS)],
  ['/me/chat', guarded(Chat)], ['/me/chat/:id', guarded(Chat)], ['/me/saved', guarded(Saved)],
  ['/me/notifications', guarded(Notifications)], ['/me/plans', guarded(Plans, LISTERS)], ['/me/verify', guarded(Verify, LISTERS)],
  ['/me/settings', guarded(Settings)], ['/admin', guarded(Admin, ['admin'])],
];

// The app shell: header, routes and the bottom tab bar.
export default function OnePropApp() {
  const { user } = useAuth();
  const loc = useLocation();
  const m = matchRoute(ROUTES, loc.path);
  const Page = m?.component;
  const [q, setQ] = useState('');
  const [unread, setUnread] = useState(0);
  const [testMode, setTestMode] = useState(false);
  // Checked on each page change so the notice appears and disappears soon after an admin flips the switch.
  useEffect(() => { let live = true; get('/v1/auth/config').then((r) => live && setTestMode(!!r.data.test_mode), () => {}); return () => { live = false; }; }, [loc.path]);
  useEffect(() => { setQ(new URLSearchParams(loc.search).get('q') || ''); }, [loc.path, loc.search]);
  // Unread badge on the bell: checked on navigation and once a minute.
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let live = true;
    const check = () => get('/v1/notifications', { page_size: 1, unread_only: true }).then((r) => live && setUnread(r.meta.unread_count), () => {});
    check();
    const t = setInterval(check, 60000);
    return () => { live = false; clearInterval(t); };
  }, [user?.id, loc.path]);

  // The role chooser is opened from the landing section ("Get started"); it is never forced on a visitor.
  const welcome = loc.path === '/welcome';
  if (welcome) return <Welcome />;

  const saved = loc.path.startsWith('/me/saved');
  const on = (p) => {
    if (p === '/') return loc.path === '/' ? 'on' : undefined;
    if (p === '/me') return (loc.path.startsWith('/me') && !saved) || loc.path.startsWith('/admin') || ['/login', '/register', '/forgot'].includes(loc.path) ? 'on' : undefined;
    return loc.path.startsWith(p) ? 'on' : undefined;
  };
  const at = (path, query) => (loc.path === path && loc.search.includes(query) ? 'on' : undefined);
  const canPost = !user || LISTERS.includes(user.role);
  const postTo = user ? '/post' : '/login?next=%2Fpost';
  const meTo = user ? (user.role === 'admin' ? '/admin' : '/me') : '/login';
  const savedTo = user ? '/me/saved' : '/login?next=%2Fme%2Fsaved';
  const submit = (e) => { e.preventDefault(); const v = q.trim(); navigate(v.length >= 2 ? `/search?q=${encodeURIComponent(v)}` : '/search'); };
  return (
    <>
      <a className="skip" href="#main">{t('ข้ามไปเนื้อหาหลัก', 'Skip to main content')}</a>
      {testMode && <div className="testbar" role="status">{t('โหมดทดสอบ: ประกาศออนไลน์ทันทีโดยยังไม่ผ่านการตรวจของทีมงาน', 'Test mode: listings go online at once and have not been reviewed by our team')}</div>}
      <header className="top">
        <div className="wrap">
          <Link className="brand" to="/" aria-label={t('หน้าแรก OneProp', 'OneProp home')}><Logo mark alt="" width="33" height="36" /><span className="wordmark" aria-hidden="true"><b>ONE</b><i>PROP</i></span></Link>
          <form className="searchpill" role="search" onSubmit={submit}>
            {Icon.search}
            <input type="text" id="top-q" aria-label={t('ค้นหาทำเล โครงการ หรือถนน', 'Search by area, project or road')} placeholder={t('ค้นหาทำเล หรือโครงการ', 'Search area or project')} value={q} onChange={(e) => setQ(e.target.value)} enterKeyHint="search" />
          </form>
          <Link className="roundbtn searchicon" to="/search" aria-label={t('ค้นหาทำเล โครงการ หรือถนน', 'Search by area, project or road')}>{Icon.search}</Link>
          <nav aria-label={t('เมนูหลัก', 'Main menu')}>
            <Link to="/search?listing_type=sale" className={at('/search', 'listing_type=sale')}>{t('ซื้อ', 'Buy')}</Link>
            <Link to="/search?listing_type=rent" className={at('/search', 'listing_type=rent')}>{t('เช่า', 'Rent')}</Link>
            <Link to="/search?group=commercial" className={at('/search', 'group=commercial')}>{t('เชิงพาณิชย์', 'Commercial')}</Link>
            <Link to="/agents" className={`navagents${on('/agents') ? ' on' : ''}`}>{t('นายหน้า', 'Agents')}</Link>
            <Link to="/guide" className={`navguide${on('/guide') ? ' on' : ''}`}>{t('คู่มือซื้อ', 'Buyer guide')}</Link>
          </nav>
          {canPost && <Link to={postTo} className="btn primary s">{Icon.plus}{t('ลงประกาศ', 'Post a listing')}</Link>}
          <ThemeButton />
          <LangButton />
          {user && <Link className="roundbtn" to="/me/notifications" aria-label={unread ? t(`การแจ้งเตือน ${unread} รายการใหม่`, `Notifications, ${unread} new`) : t('การแจ้งเตือน', 'Notifications')}>{Icon.bell}{unread > 0 && <span className="dot" />}</Link>}
          <Link className="roundbtn avatar" to={meTo} aria-label={user ? t('บัญชีของฉัน', 'My account') : t('เข้าสู่ระบบ', 'Sign in')}>{user ? user.profile.full_name.trim().charAt(0) : Icon.user}</Link>
        </div>
      </header>
      <main id="main" tabIndex={-1}><div className="wrap">{Page ? <Page key={loc.path} params={m.params} /> : <div className="empty"><h2>{t('ไม่พบหน้านี้', 'Page not found')}</h2><Link className="btn primary" to="/">{t('กลับหน้าแรก', 'Back to home')}</Link></div>}</div></main>
      {!loc.path.startsWith('/post') && !loc.path.startsWith('/me/chat') && !loc.path.startsWith('/admin') && <Footer />}
      <nav className="tabbar" aria-label={t('เมนูล่าง', 'Bottom menu')}>
        <Link to="/" className={loc.path === '/' || loc.path === '/search' ? 'on' : undefined}>{Icon.search}{t('สำรวจ', 'Explore')}</Link>
        {user?.role !== 'admin' && <Link to={savedTo} className={saved ? 'on' : undefined}>{Icon.bookmark}{t('ที่บันทึก', 'Saved')}</Link>}
        {canPost && <Link to={postTo} className={`post${loc.path.startsWith('/post') ? ' on' : ''}`}>{Icon.plus}{t('ลงประกาศ', 'Post')}</Link>}
        {user?.role !== 'admin' && <Link to={user ? '/me/chat' : '/login?next=%2Fme%2Fchat'} className={on('/me/chat')}>{Icon.chat}{t('ข้อความ', 'Messages')}</Link>}
        <Link to={meTo} className={loc.path.startsWith('/me') && !loc.path.startsWith('/me/chat') && !saved ? 'on' : loc.path === '/admin' ? 'on' : undefined}>{Icon.user}{user ? t('บัญชี', 'Account') : t('เข้าสู่ระบบ', 'Sign in')}</Link>
      </nav>
    </>
  );
}
